import { cloneDeep } from 'lodash';
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import plusSvg from 'assets/icons/plus.svg';
import SectionButton from 'lib/SectionButton';
import { SectionButtonTheme } from 'lib/SectionButton/SectionButton.types';
import { Store } from 'components/common/types/Store.types';
import { addObjective, updateProductObjectives } from 'store/productManagement/reducer';
import { PlannerSecondaryPanelType } from 'store/dealManagement/types';
import { changePlannerSecondaryPanelType } from 'store/dealManagement/reducer';
import { updateObjectiveToExplode, updateIsExplodeModalOpen, updateObjectiveToRemove } from 'store/objective/reducer';
import { CodeNameModel } from 'components/common/types';
import {
  DirectSalesLineObjective,
  DirectSalesObjectiveFilterType,
  DirectSalesObjectiveToRemove,
} from 'components/common/types/DirectSalesCampaign.types';
import ObjectivePanel from 'components/pages/Planner/PlannerSections/Objectives/ObjectivePanel';
import ObjectiveSelection from 'components/pages/Planner/PlannerSections/Objectives/ObjectiveSelection';
import useObjectives, {
  createDefaultObjective,
  createNewObjectiveFromExisting,
} from 'components/pages/Planner/PlannerSections/Objectives/useObjectives';
import ObjectiveModals from 'components/pages/Planner/PlannerSections/Objectives/ObjectiveModals';
import { getSectionButtonStatus } from 'components/pages/Planner/PlannerSections/Objectives/utils/getSectionButtonStatus';
import ObjectiveSubPanels from 'components/pages/Planner/PlannerSections/Objectives/ObjectiveSubPanels';
import useObjectivesEllipses from 'components/pages/Planner/PlannerSections/Objectives/useObjectiveElipsis';

type ObjectivesProps = {
  lineObjectives: DirectSalesLineObjective[];
};

const ProductObjectivePanelsWrapper: React.FC<ObjectivesProps> = ({ lineObjectives }) => {
  const dispatch = useDispatch();

  const {
    data: activeObjective,
    data: { objectiveGroupId: activeObjectiveGroupId },
  } = useSelector((state: Store) => state.objective);
  const plannerSecondaryPanelType = useSelector((state: Store) => state.dealManagement.plannerSecondaryPanelType);

  const {
    objectives,
    initialObjectiveState,
    handleChangeActiveObjective,
    handleClosePanel,
    onFilterChange,
    isObjectiveChange,
    closeObjectivePanel,
    openObjectivePanel,
    confirmObjectiveChange,
    confirmDiscardChanges,
    handleExplodeObjectiveValidation,
    explodeObjective,
  } = useObjectives({ lineObjectives });

  const areEllipsisMenusDisabled = useObjectivesEllipses({ initialObjectiveState });

  const handleDuplicateObjective = (objective: DirectSalesLineObjective): void => {
    const { newObjective, existingObjectives } = createNewObjectiveFromExisting(objective, lineObjectives);

    dispatch(updateProductObjectives([...existingObjectives, newObjective]));
  };

  const onEditObjective = (objective: DirectSalesLineObjective): void => {
    const objectiveIdx = objectives.findIndex(
      ({ objectiveGroupId }) => objectiveGroupId === objective.objectiveGroupId,
    );
    if (objectiveIdx !== -1) {
      const clonedObjectives = cloneDeep(objectives);
      clonedObjectives[objectiveIdx] = objective;
      dispatch(updateProductObjectives(clonedObjectives));
    }
  };

  const onConfirm = (): void => {
    const foundIndex = lineObjectives.findIndex(
      ({ objectiveGroupId }) => activeObjective.objectiveGroupId === objectiveGroupId,
    );

    if (foundIndex !== -1) {
      const clonedObjectives = cloneDeep(lineObjectives);
      clonedObjectives[foundIndex] = activeObjective;

      dispatch(updateProductObjectives(clonedObjectives));
    } else {
      dispatch(updateProductObjectives([...lineObjectives, activeObjective]));
    }

    dispatch(changePlannerSecondaryPanelType(PlannerSecondaryPanelType.NONE));
  };

  const confirmModal = useCallback(
    async (objective: DirectSalesObjectiveToRemove, callbackFunction: (objective: CodeNameModel | null) => void) => {
      if (!objective) return;

      const objectiveId = Number(objective.code);

      if (objectiveId === activeObjectiveGroupId) closeObjectivePanel();

      const newObjectives = lineObjectives
        .filter((obj) => obj.objectiveGroupId !== Number(objective.code))
        .map((obj) => ({ ...obj, objectiveStatus: undefined }));

      dispatch(updateProductObjectives(newObjectives));

      callbackFunction(null);
    },
    [lineObjectives, activeObjectiveGroupId],
  );

  const onAddNewObjective = (): void => {
    const newObjective = createDefaultObjective(lineObjectives);

    dispatch(addObjective(newObjective));
    openObjectivePanel(newObjective);
  };

  const onClearObjective = (objective: DirectSalesLineObjective): void => {
    dispatch(
      updateObjectiveToRemove({
        code: objective.objectiveGroupId.toString(),
        name: objective.name,
      }),
    );
  };

  const confirmExplodeObjective = useCallback(
    (objective: DirectSalesLineObjective, objectiveFilterType: DirectSalesObjectiveFilterType) => {
      explodeObjective(objective, objectiveFilterType, (newObjectives) =>
        dispatch(updateProductObjectives(newObjectives)),
      );

      dispatch(updateIsExplodeModalOpen(false));
      dispatch(updateObjectiveToExplode({ objective: null, objectiveFilterType: null }));
    },
    [explodeObjective],
  );

  const handleValidateAndExplodeObjective = useCallback(
    (explodedObjective: DirectSalesLineObjective, objectiveFilter: DirectSalesObjectiveFilterType) =>
      handleExplodeObjectiveValidation(
        explodedObjective,
        objectiveFilter,
        (newObjectives: DirectSalesLineObjective[]) => dispatch(updateProductObjectives(newObjectives)),
      ),
    [handleExplodeObjectiveValidation],
  );

  return (
    <>
      <ObjectiveModals
        confirmObjectiveChange={confirmObjectiveChange}
        confirmDiscardChanges={confirmDiscardChanges}
        confirmModal={confirmModal}
        confirmExplodeObjective={confirmExplodeObjective}
        objectives={objectives}
      />
      {objectives.map((objective) => (
        <SectionButton
          key={objective.objectiveGroupId}
          dataTestId={`section-button-objective-${objective.objectiveGroupId}`}
          label={objective.name}
          theme={SectionButtonTheme.SOLID}
          onClick={() => handleChangeActiveObjective(objective)}
          isActive={objective.objectiveGroupId === activeObjectiveGroupId}
          isEllipsisDisabled={areEllipsisMenusDisabled}
          onClear={() => onClearObjective(objective)}
          onDuplicate={() => handleDuplicateObjective(objective)}
          clearButtonLabel="Delete"
          sectionGroupStatus={getSectionButtonStatus(objective.objectiveStatus)}
          enableAccordionToggle
          extendedChildren={
            <ObjectiveSelection
              objectives={objectives}
              objective={objective}
              onClick={() => handleChangeActiveObjective(objective)}
              onEditObjective={onEditObjective}
              areEllipsisMenusDisabled={areEllipsisMenusDisabled}
              lineObjectives={lineObjectives}
              isClearAllDisabled
              onExplode={handleValidateAndExplodeObjective}
              isProductObjective
            />
          }
        />
      ))}
      <SectionButton
        dataTestId="section-button-add-objective"
        label="Add objective"
        onClick={onAddNewObjective}
        theme={SectionButtonTheme.DASHED}
        icon={plusSvg}
      />
      {initialObjectiveState && plannerSecondaryPanelType === PlannerSecondaryPanelType.OBJECTIVE && (
        <ObjectivePanel
          activeObjective={activeObjective}
          initialObjectiveState={initialObjectiveState}
          isChange={isObjectiveChange}
          onClose={handleClosePanel}
          onConfirm={onConfirm}
        />
      )}
      <ObjectiveSubPanels
        isSecondaryPanelDisplayed
        handleClosePanel={handleClosePanel}
        onFilterChange={onFilterChange}
      />
    </>
  );
};

export default ProductObjectivePanelsWrapper;
